/**************
 LP Product (landing page buy box)
 ***************/

/*
 Drives sections/main-product-lp-v1.liquid.

 Sells a NATIVE Shopify component bundle, so the cart side is deliberately tiny compared with
 main-product-v1.js: one parent variant, one line item, no `_bundle` / `_bundle_id` line-item
 properties, no Discount Kit percentage to route, and no client-side bundle-total maths. The
 bundle's own price is the discount, so there is nothing here that could disagree with checkout.

 What it does own:
 - gallery swiper + thumbnail strip, with videos playing only while their slide is on screen
 - offer switching (each offer names its own product, so a kit and a single-product downsell
   can share the buy box)
 - option -> variant resolution and price rendering for whichever offer is selected
 - a mobile sticky add-to-cart bar, driven from this same closure so it can never disagree with
   the real button about price or variant

 Adds go through the global addProduct() from theme-global.js — that is the single chokepoint
 which stamps split-test attribution onto the cart, so never fetch /cart/add.js directly.
*/

(function () {
    // section id comes off this script tag; attribute selector rather than `#id` because a
    // Shopify section id can begin with a digit, which is an invalid CSS id selector
    const sectionID = document.currentScript.dataset.id;
    const section = document.querySelector(`[id="${sectionID}"]`);

    if (!section) {
        console.warn(`LP product section with ID ${sectionID} not found.`);
        return;
    }

    const atcButton = section.querySelector(".productLpV1Atc");
    const offerElements = Array.from(section.querySelectorAll(".productLpV1Offer"));
    const optionGroups = Array.from(section.querySelectorAll(".productLpV1OptionsGroup"));
    const sticky = section.querySelector("[data-sticky-atc]");

    // nothing to sell (no product picked, or no offers configured yet in the editor)
    if (!atcButton || !offerElements.length) {
        return;
    }

    // one trimmed payload per offer, keyed by the offer's index
    const offers = offerElements.map(function (element) {
        const json = element.querySelector(".productLpV1OfferJSON");
        let data = null;
        try {
            data = JSON.parse(json.textContent);
        } catch (error) {
            console.error("LP product: could not parse offer JSON", error);
        }
        return {
            index: parseInt(element.dataset.offer, 10),
            element: element,
            data: data
        };
    }).filter(offer => offer.data);

    if (!offers.length) {
        return;
    }

    let activeOffer = offers.find(offer => offer.element.querySelector(".productLpV1OfferHeaderInput").checked) || offers[0];
    let activeVariant = null;

    /**************
     Gallery
     ***************/

    const mainEl = section.querySelector(".productLpV1VisualMain");
    const thumbsEl = section.querySelector(".productLpV1VisualThumbs");
    let mainSwiper = null;

    function initGallery() {
        if (!mainEl || typeof Swiper === "undefined") {
            return;
        }

        const slideCount = mainEl.querySelectorAll(".productLpV1VisualMainSlide").length;

        // a single slide needs no carousel, pagination or thumbnail strip
        if (slideCount < 2) {
            thumbsEl?.remove();
            mainEl.querySelector(".productLpV1VisualPagination")?.remove();
            return;
        }

        const thumbsSwiper = thumbsEl
            ? new Swiper(thumbsEl, {
                slidesPerView: 4,
                slidesPerGroup: 1,
                spaceBetween: 10,
                watchOverflow: true,
                // the slides are <button>s carrying their own aria-labels
                a11y: false,
                breakpoints: {
                    801: {slidesPerView: 5}
                }
            })
            : null;

        function syncThumb(index) {
            if (!thumbsEl) {
                return;
            }
            thumbsEl.querySelector(".productLpV1VisualThumbsSlide.active")?.classList.remove("active");
            const thumb = thumbsEl.querySelectorAll(".productLpV1VisualThumbsSlide")[index];
            thumb?.classList.add("active");
            // keep the active thumb framed without yanking the strip around
            if (thumbsSwiper && !thumbsSwiper.isLocked) {
                const perView = Math.ceil(thumbsSwiper.params.slidesPerView);
                const maxFirst = Math.max(0, thumbsSwiper.slides.length - perView);
                const first = thumbsSwiper.activeIndex;
                let target = first;
                if (index > first + perView - 2) {
                    target = index - perView + 2;
                } else if (index < first + 1) {
                    target = index - 1;
                }
                target = Math.max(0, Math.min(target, maxFirst));
                if (target !== first) {
                    thumbsSwiper.slideTo(target);
                }
            }
        }

        mainSwiper = new Swiper(mainEl, {
            slidesPerView: 1,
            spaceBetween: 0,
            autoHeight: true,
            pagination: {
                el: mainEl.querySelector(".productLpV1VisualPagination"),
                clickable: true
            },
            /*
             Re-measure when the container or its parents change size. Without this the gallery
             measures once, and on mobile the buy box switches from a grid column to a flex column
             AFTER Swiper has initialised — leaving slides sized against the old width.
            */
            observer: true,
            observeParents: true,
            observeSlideChildren: true,
            on: {
                slideChange: function () {
                    syncThumb(this.activeIndex);
                }
            }
        });

        // images finish decoding after init, and autoHeight measured the pre-load height
        mainEl.querySelectorAll("img").forEach(function (image) {
            if (image.complete) {
                return;
            }
            image.addEventListener("load", () => mainSwiper && mainSwiper.update(), {once: true});
        });
        window.addEventListener("load", () => mainSwiper && mainSwiper.update(), {once: true});

        thumbsEl?.querySelectorAll(".productLpV1VisualThumbsSlide").forEach(function (thumb) {
            thumb.addEventListener("click", function () {
                mainSwiper.slideTo(parseInt(this.dataset.index, 10));
            });
        });

        syncThumb(mainSwiper.activeIndex);
    }

    /*
     Play a gallery video only while its slide is actually on screen. Swiper translates
     non-active slides horizontally out of the viewport, so viewport intersection is a good
     proxy for "this slide is showing" — no need to hook slideChange as well.
    */
    function initGalleryVideos() {
        const videos = section.querySelectorAll(".productLpV1VisualMainSlideVideo");
        if (!videos.length) {
            return;
        }

        if (!("IntersectionObserver" in window)) {
            videos.forEach(video => video.play().catch(() => {}));
            return;
        }

        const observer = new IntersectionObserver(function (entries) {
            entries.forEach(function (entry) {
                if (entry.isIntersecting) {
                    // preload="none" means the first play() is also the first byte fetched
                    entry.target.play().catch(() => {});
                } else {
                    entry.target.pause();
                }
            });
        }, {threshold: 0.25});

        videos.forEach(video => observer.observe(video));
    }

    /**************
     Options -> variant
     ***************/

    // rows in option order, so the values line up with variant.options positionally
    function optionRowsFor(offer) {
        const group = optionGroups.find(candidate => parseInt(candidate.dataset.offer, 10) === offer.index);
        if (!group) {
            return [];
        }
        return Array.from(group.querySelectorAll(".productLpV1OptionsRow"))
            .sort((a, b) => parseInt(a.dataset.optionIndex, 10) - parseInt(b.dataset.optionIndex, 10));
    }

    function resolveVariant(offer) {
        const rows = optionRowsFor(offer);

        // no options (or a default-variant product): first sellable variant wins
        if (!rows.length) {
            return offer.data.variants.find(variant => variant.available) || offer.data.variants[0] || null;
        }

        const selected = rows.map(function (row) {
            const input = row.querySelector(".productLpV1OptionsRowValuesItemInput:checked");
            return input ? input.value : null;
        });

        // a row with nothing chosen (every value sold out) can't resolve to a variant
        if (selected.some(value => value === null)) {
            return null;
        }

        return offer.data.variants.find(function (variant) {
            return JSON.stringify(variant.options) === JSON.stringify(selected);
        }) || null;
    }

    function renderSelectedLabels(offer) {
        optionRowsFor(offer).forEach(function (row) {
            const input = row.querySelector(".productLpV1OptionsRowValuesItemInput:checked");
            const label = row.querySelector("[data-selected-label]");
            if (label) {
                label.textContent = input ? input.value : "";
            }
        });
    }

    /**************
     Price
     ***************/

    /*
     What the shopper actually pays for a variant, after this offer's automatic discount.

     Shopify FLOORS the discount amount rather than rounding the resulting price — 2999 at 25% is
     a 749 discount, not 750. Rounding the price instead quotes a cent less than the shopper is
     charged, which is the one direction a price display may never be wrong in. This mirrors the
     Liquid in sections/main-product-lp-v1.liquid; the two must agree or the page contradicts
     itself on a variant change.

     The depth comes from the offer payload, which Liquid filled from the discount_display
     metaobject the sync Worker maintains. 0 — the state before any rule exists — collapses this
     to the variant's own price, which is the theme's current behaviour exactly.
    */
    function payable(variant, offer) {
        const percent = parseFloat(offer?.data?.discountPercent) || 0;
        if (!variant || percent <= 0) {
            return variant ? variant.price : 0;
        }
        return variant.price - Math.floor((variant.price * percent) / 100);
    }

    /* The struck-through anchor: the compare-at where one is set, otherwise the price itself. */
    function anchor(variant) {
        return Math.max(variant.compare_at_price || 0, variant.price || 0);
    }

    function renderPrice() {
        const compareEl = activeOffer.element.querySelector("[data-offer-compare]");
        const priceEl = activeOffer.element.querySelector("[data-offer-price]");

        const sellable = activeVariant && activeVariant.available;
        const final = sellable ? payable(activeVariant, activeOffer) : 0;
        const was = sellable ? anchor(activeVariant) : 0;
        const priceText = sellable ? price(final, true) : "";
        const compareText = sellable && was > final ? price(was, true) : "";

        if (priceEl) {
            priceEl.textContent = sellable ? priceText : (activeVariant ? "Sold Out" : "Unavailable");
        }
        if (compareEl) {
            // the amount goes beside the visually hidden "Regular price" label, not over it
            (compareEl.querySelector("[data-compare-amount]") || compareEl).textContent = compareText;
            compareEl.hidden = !compareText;
        }

        renderStickyLabel(priceText);
    }

    /*
     The sticky button's label is a template holding {price} and/or {savings}, filled from the same
     state the buy box renders from — so the bar can never advertise a figure the offer above it
     doesn't agree with.

     Savings is rounded the way the rest of the theme rounds it: nearest whole percent, then up to
     the next multiple of 5 (see theme-mini-cart.js renderFooter and pdp-upsell.liquid).
    */
    const stickyButton = sticky?.querySelector("[data-sticky-button]");
    const stickyLabelTemplate = stickyButton?.dataset.label || "";

    function renderStickyLabel(priceText) {
        if (!stickyButton || !stickyLabelTemplate) {
            return;
        }

        let savingsText = "";
        if (activeVariant) {
            // measured against what is actually charged, so the bar cannot advertise a saving the
            // offer above it no longer shows once the discount moves into Shopify
            const was = anchor(activeVariant);
            const final = payable(activeVariant, activeOffer);
            if (was > final) {
                const raw = Math.round(((was - final) / was) * 100);
                savingsText = `${Math.ceil(raw / 5) * 5}%`;
            }
        }

        let label = stickyLabelTemplate
            .replace(/\{price\}/g, priceText)
            .replace(/\{savings\}/g, savingsText);

        // a template that was entirely "Save {savings} ..." with nothing to save would read oddly,
        // so fall back to the plain add-to-cart wording rather than shipping a gap
        if (!savingsText && /\{savings\}/.test(stickyLabelTemplate)) {
            label = addToCartText;
        }

        stickyButton.textContent = label.replace(/\s{2,}/g, " ").trim();
    }

    // from data attributes, not from the rendered label: a product that starts sold out has the
    // sold-out copy in the span, leaving nothing to restore once a sellable variant is picked
    const addToCartText = atcButton.dataset.atcText || "Add To Cart";
    const soldOutText = atcButton.dataset.soldOutText || "Sold Out";
    const unavailableText = atcButton.dataset.unavailableText || "Unavailable";

    function renderAtcState() {
        const sellable = !!(activeVariant && activeVariant.available);
        const text = section.querySelector(".productLpV1AtcText");

        atcButton.disabled = !sellable;

        if (!text) {
            return;
        }

        // `activeVariant` set but unavailable means a real sold-out variant; null means the chosen
        // colour combination has no variant at all, which reads differently to a shopper
        if (sellable) {
            text.textContent = addToCartText;
        } else {
            text.textContent = activeVariant ? soldOutText : unavailableText;
        }
    }

    /*
     Swap the gallery's lead slide to the selected variant's image.

     The slide is swapped in place rather than added/removed: mutating a live Swiper's slide list
     mid-interaction is what makes carousels jump to the wrong index. Everything after slide 1 is
     media shared across all variants, so it never needs touching.

     Also jumps the gallery back to slide 1 on a colour change, since the point of the change is to
     see the new colour — but only when the shopper is already looking at the lead slide's
     neighbourhood, so it can't yank them out of a video they're watching.
    */
    const leadImage = section.querySelector("[data-variant-image]");
    const leadThumb = section.querySelector("[data-variant-thumb-image]");

    function renderVariantImage() {
        const image = activeVariant && activeVariant.image;
        if (!leadImage || !image) {
            return;
        }
        if (leadImage.getAttribute("src") === image.src) {
            return;
        }

        leadImage.setAttribute("src", image.src);
        leadImage.setAttribute("srcset", image.srcset || "");
        leadImage.setAttribute("alt", image.alt || "");
        if (image.width) {
            leadImage.setAttribute("width", image.width);
        }
        if (image.height) {
            leadImage.setAttribute("height", image.height);
        }
        if (leadThumb && image.thumb) {
            leadThumb.setAttribute("src", image.thumb);
            leadThumb.removeAttribute("srcset");
        }

        if (mainSwiper && mainSwiper.activeIndex !== 0) {
            mainSwiper.slideTo(0);
        }
    }

    function update() {
        activeVariant = resolveVariant(activeOffer);
        renderSelectedLabels(activeOffer);
        renderVariantImage();
        renderPrice();
        renderAtcState();
    }

    /**************
     Offer switching
     ***************/

    function setActiveOffer(index) {
        const next = offers.find(offer => offer.index === index);
        if (!next) {
            return;
        }
        activeOffer = next;

        offerElements.forEach(function (element) {
            element.classList.toggle("active", parseInt(element.dataset.offer, 10) === index);
        });

        // each offer has its own option rows (a kit has two colours, a single product has one),
        // so only the selected offer's rows are shown
        optionGroups.forEach(function (group) {
            group.classList.toggle("active", parseInt(group.dataset.offer, 10) === index);
        });

        update();
    }

    section.querySelectorAll(".productLpV1OfferHeaderInput").forEach(function (input) {
        input.addEventListener("change", function () {
            if (this.checked) {
                setActiveOffer(parseInt(this.value, 10));
            }
        });
    });

    section.querySelectorAll(".productLpV1OptionsRowValuesItemInput").forEach(function (input) {
        input.addEventListener("change", update);
    });

    /**************
     Add to cart
     ***************/

    /*
     After a successful add this goes STRAIGHT TO CHECKOUT by default rather than opening the drawer.

     On a single-offer ad landing page the drawer has nothing useful to add: there is no cross-sell to
     make, the shopper already chose their colours and their offer, and every extra screen between the
     tap and the payment form is somewhere to lose them. The drawer remains available behind the
     setting for when a page does want a cart step.

     The add still goes through addProduct(), so the split-test attribution stamp lands before the
     shopper leaves the page — skipping it and posting a form straight to /cart/add would drop that.
    */
    const goToCheckout = atcButton.dataset.afterAdd !== "drawer";
    const clearCartFirst = atcButton.dataset.clearCart === "true";

    /*
     Note the asymmetry, which is deliberate. Emptying a basket is unrecoverable, so it is opt-IN
     (`=== "true"`): absent or malformed markup must not destroy a cart. Clearing a discount code
     is not destructive, and NOT clearing one lets a shopper take a further cut off the price this
     page advertised — so it is opt-OUT (`!== "false"`): absent markup, e.g. a stale cached
     section, still does the safe thing.
    */
    const clearDiscountsFirst = atcButton.dataset.clearDiscounts !== "false";

    /*
     Drops any discount code the shopper arrived carrying.

     A code lives on the CART, not merely in a cookie, and survives /cart/clear.js — verified on
     this store. So emptying the basket does NOT remove it, and a shopper who arrived with the
     sitewide code would buy this page's offer at a further cut off the advertised price. Only
     /cart/update.js can drop them, which is why this is a second call rather than a flag on the
     first, and why it is gated separately from the cart clear.

     Failures are swallowed: pricing hygiene is not worth losing the sale over.
    */
    async function clearDiscountCodes() {
        try {
            await updateCart({discount: ""});
        } catch (error) {
            console.error("LP product: discount clear failed, continuing", error);
        }
    }

    /*
     Empties the cart so this page's checkout contains only this offer.

     Two things learned the hard way and worth keeping written down:

     1. /cart/clear.js REQUIRES a request body. A bodyless POST answers 411 Length Required, so
        the `{}` and the Content-Type header are both load-bearing.
     2. It clears line items but NOT cart attributes, so the split-test stamp that addProduct()
        writes survives — which is why this runs BEFORE the add rather than after.

     A failure here is deliberately swallowed. Isolating the cart is a measurement nicety; the
     sale is not. If the clear fails the add still goes ahead and the shopper checks out with a
     mixed cart, which is the old behaviour rather than a broken page.
    */
    async function clearCart() {
        try {
            await fetch(`${Shopify.routes.root}cart/clear.js`, {
                method: "POST",
                headers: {"Content-Type": "application/json"},
                body: "{}"
            });
        } catch (error) {
            console.error("LP product: cart clear failed, adding anyway", error);
        }
    }

    async function addToCart() {
        if (!activeVariant || !activeVariant.available) {
            return;
        }

        atcButton.classList.add("loading");
        atcButton.disabled = true;

        try {
            /*
             Codes before items, always. Failing that way leaves the old items and no code — a
             mixed cart, which is merely the old behaviour. The other order fails into an empty
             cart still carrying a code, which is precisely the state this exists to prevent.
            */
            if (clearDiscountsFirst) {
                await clearDiscountCodes();
            }

            if (clearCartFirst) {
                await clearCart();
            }

            // one line: a native bundle expands into its components at checkout and on the
            // order, so the theme never assembles the components itself
            const response = await addProduct({items: [{id: activeVariant.id, quantity: 1}]});

            // /cart/add.js answers 4xx with a body carrying `status` rather than throwing —
            // an out-of-stock bundle component surfaces here, so it must not be swallowed
            if (response && response.status) {
                throw new Error(response.description || response.message || "Add to cart failed");
            }

            if (goToCheckout) {
                /*
                 Return before the finally block so the button keeps its loading state while the
                 browser navigates — clearing it would flash the button back to idle and invite a
                 second tap that adds a second kit.

                 Shopify.routes.root rather than a bare "/checkout" so a market or locale subpath is
                 preserved, matching how theme-global.js builds its cart URLs.
                */
                // Shoplift checkout test (snippets/checkout-test-gate.liquid): stamp the cart's arm
                // before leaving. Always resolves, within its own cap, so it can never hold checkout.
                if (window.checkoutTestStamp) {
                    await window.checkoutTestStamp();
                }
                window.location.href = `${Shopify.routes.root}checkout`;
                return;
            }

            await refreshCart();
            await openCart();
        } catch (error) {
            console.error("LP product: add to cart failed", error);
            const text = section.querySelector(".productLpV1AtcText");
            if (text) {
                const original = text.textContent;
                text.textContent = "Something went wrong — try again";
                setTimeout(() => {
                    text.textContent = original;
                }, 3000);
            }
        } finally {
            atcButton.classList.remove("loading");
            renderAtcState();
        }
    }

    atcButton.addEventListener("click", addToCart);
    /*
     The sticky CTA is deliberately NOT wired to addToCart. It's an anchor to the buy box: the bar
     only appears while the offer is off screen, so the shopper still has colours to pick, and adding
     for them would choose on their behalf. Its label still tracks live price/savings so the promise
     matches the offer it scrolls to.
    */

    /**************
     Sticky bar
     ***************/

    /*
     Revealed once the shopper is past the first screen AND the real button is off screen; hidden
     again whenever the real button is visible, so the two are never on screen together. CSS keeps
     it to mobile only — desktop already has the LP header's persistent BUY NOW.
    */
    function initSticky() {
        if (!sticky) {
            return;
        }

        // rendered with `hidden` so a no-JS visitor never sees a button that can't work
        sticky.hidden = false;

        let atcVisible = true;
        let scrolledEnough = false;
        let ticking = false;

        function paint() {
            ticking = false;
            sticky.classList.toggle("active", scrolledEnough && !atcVisible);
        }

        function request() {
            if (ticking) {
                return;
            }
            ticking = true;
            window.requestAnimationFrame(paint);
        }

        if ("IntersectionObserver" in window) {
            new IntersectionObserver(function (entries) {
                atcVisible = entries[0].isIntersecting;
                request();
            }, {threshold: 0}).observe(atcButton);
        }

        window.addEventListener("scroll", function () {
            scrolledEnough = window.scrollY > window.innerHeight * 0.6;
            request();
        }, {passive: true});
    }

    /**************
     Init
     ***************/

    initGallery();
    initGalleryVideos();
    setActiveOffer(activeOffer.index);
    initSticky();
}());
